import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, UserX, Ban, Trophy, Zap, Flame, Activity, CalendarRange, Coins, Gift,
} from 'lucide-react';
import {
  fetchFriendDetail, removeFriend, blockUser, buildAvatarUrl,
  type FriendDetail,
} from '@/lib/api';
import { BadgeIcon, type BadgeColorTier } from '@/components/ui/BadgeIcon';
import { StatusPill } from '@/components/primitives';
import { GiftPointsSheet } from './GiftPointsSheet';
import { toast } from '@/store/toast';
import styles from './FriendDetailPage.module.css';

interface Props {
  userId: string;
  onNavigate: (path: string) => void;
}

/** Translate fn for the 'friendDetail' namespace, aliased so the
 *  module-scope displayName helper can accept `t` without another import. */
type FriendDetailT = ReturnType<typeof useTranslation>['t'];

/**
 * Per-friend detail page. Reached from FriendsPage when the
 * caller taps an accepted-friendship row. Backend gates this on
 * accepted-friendship status — direct URL paste for arbitrary
 * userIds returns 404, which this page renders as "Friend not
 * found" rather than as a privacy leak.
 *
 * Stats shown are deliberately curated to social signals:
 *   - rank, total kWh, total sessions, streak, badges
 *   - friends-since date, mutual friends count
 * Excluded by design (feels surveillant between friends):
 *   - which chargers / locations they visited
 *   - per-session detail (kWh per session, spending)
 *   - last-seen timestamps
 *   - wallet / spending
 *
 * Block + Remove actions both confirm via window.confirm —
 * matches the existing FriendsPage Remove button convention
 * (FriendsPage.tsx:457). A dedicated sheet primitive is a
 * future polish; matching convention beats introducing one
 * pattern just for this page.
 */
export function FriendDetailPage({ userId, onNavigate }: Props) {
  const { t } = useTranslation('friendDetail');
  const [pending, setPending] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const detailQ = useQuery<FriendDetail>({
    queryKey: ['friends', userId, 'detail'],
    queryFn: () => fetchFriendDetail(userId),
    staleTime: 30_000,
    retry: false,
  });

  const handleRemove = async () => {
    if (!detailQ.data) return;
    if (!window.confirm(t('confirmRemove', { name: displayName(detailQ.data, t) }))) return;
    setPending(true);
    try {
      await removeFriend(detailQ.data.friendshipId);
      onNavigate('/friends');
    } catch (err) {
      setPending(false);
      toast(err instanceof Error ? err.message : t('toastRemoveError'), { tone: 'error' });
    }
  };

  const handleBlock = async () => {
    if (!detailQ.data) return;
    const name = displayName(detailQ.data, t);
    const ok = window.confirm(t('confirmBlock', { name }));
    if (!ok) return;
    setPending(true);
    try {
      await blockUser(userId);
      onNavigate('/friends');
    } catch (err) {
      setPending(false);
      toast(err instanceof Error ? err.message : t('toastBlockError'), { tone: 'error' });
    }
  };

  // 404 + error path lands on a "not found" view. Direct URL
  // paste against a non-friend userId hits this branch (the
  // backend returns 404 to enforce the privacy floor).
  if (detailQ.isError) {
    return (
      <div className={styles.page}>
        <button className={styles.backFab} onClick={() => onNavigate('/friends')} aria-label={t('back')}>
          <ArrowLeft size={16} />
        </button>
        <div className={styles.notFoundCard}>
          <p className={styles.notFoundTitle}>{t('notFound.title')}</p>
          <p className={styles.notFoundDesc}>
            {t('notFound.desc')}
          </p>
          <button className={styles.primaryBtn} onClick={() => onNavigate('/friends')}>
            {t('notFound.backBtn')}
          </button>
        </div>
      </div>
    );
  }

  if (detailQ.isLoading || !detailQ.data) {
    return (
      <div className={styles.page}>
        <button className={styles.backFab} onClick={() => onNavigate('/friends')} aria-label={t('back')}>
          <ArrowLeft size={16} />
        </button>
        <p className={styles.loading}>{t('loading')}</p>
      </div>
    );
  }

  const detail = detailQ.data;
  const friendsSinceLabel = detail.friendsSince
    ? new Date(detail.friendsSince).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    })
    : null;

  return (
    <div className={styles.page}>
      <button className={styles.backFab} onClick={() => onNavigate('/friends')} aria-label={t('back')}>
        <ArrowLeft size={16} />
      </button>

      {/* Header — avatar + name + rank pill + friends-since */}
      <div className={styles.header}>
        <div className={styles.avatarWrap}>
          {detail.user.avatarStyle && detail.user.avatarSeed ? (
            <img
              src={buildAvatarUrl(detail.user.avatarStyle as never, detail.user.avatarSeed)}
              alt=""
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarFallback}>
              {(detail.user.username ?? detail.user.name ?? '?').slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <h1 className={styles.title}>{displayName(detail, t)}</h1>
        {detail.user.username && detail.user.username !== detail.user.name && (
          <p className={styles.handle}>@{detail.user.username}</p>
        )}
        {detail.stats.currentRank && (
          <StatusPill tone="mute" variant="soft" size="sm">
            {detail.stats.currentRank.toUpperCase()}
          </StatusPill>
        )}
        {friendsSinceLabel && (
          <p className={styles.since}>{t('friendsSince', { date: friendsSinceLabel })}</p>
        )}
      </div>

      {/* Stats grid — six tiles, 2×3 at phone width: kWh/sessions,
          daily streak/weekly streak, badges/points. The weekly tile
          (CalendarRange — the flame is the daily streak's icon) and
          the points tile always render, including at 0, so the grid
          never collapses to an orphaned five. */}
      <div className={styles.statsGrid}>
        <StatTile
          icon={<Zap size={14} />}
          label="kWh"
          value={detail.stats.totalKwh.toFixed(1)}
        />
        <StatTile
          icon={<Activity size={14} />}
          label={t('stats.sessions')}
          value={String(detail.stats.totalSessions)}
        />
        <StatTile
          icon={<Flame size={14} />}
          label={t('stats.streak')}
          value={detail.stats.streakDays > 0 ? `${detail.stats.streakDays}d` : '—'}
        />
        <StatTile
          icon={<CalendarRange size={14} />}
          label={t('stats.streakWeek')}
          value={`${detail.stats.streakWeeks}w`}
        />
        <StatTile
          icon={<Trophy size={14} />}
          label={t('stats.badges')}
          value={String(detail.stats.badgeCount)}
        />
        <StatTile
          icon={<Coins size={14} />}
          label={t('stats.points')}
          value={detail.stats.totalPoints.toFixed(0)}
        />
      </div>

      {/* Shared pair streak — the streak YOU AND this friend hold
          together, distinct from the friend's own solo "Streak (wk)"
          tile in the grid above. Rendered only while LIVE (liveness
          already requires weeks >= 1), so a lapsed or never-started
          pair shows nothing. The perk line states the live-pair gift
          cap the server enforces. */}
      {detail.pairStreak?.live && (
        <div className={styles.pairBanner} role="status">
          <span className={styles.pairBannerIcon} aria-hidden>
            <CalendarRange size={16} />
          </span>
          <span className={styles.pairBannerText}>
            <span className={styles.pairBannerTitle}>
              {t('pairBanner.title', { name: displayName(detail, t) })}{' '}
              <strong>{t('pairBanner.streak', { weeks: detail.pairStreak.currentWeeks })}</strong>
            </span>
            <span className={styles.pairBannerPerk}>
              {t('pairBanner.perk')}
            </span>
          </span>
        </div>
      )}

      {/* Gift points — the page's first positive action (remove and
          block stay in the destructive tail). The sheet owns the
          amount, the giver's balance, and surfaces the server's
          friendship / cap / balance rejections verbatim. */}
      <button
        type="button"
        className={styles.giftBtn}
        onClick={() => setGiftOpen(true)}
        disabled={pending}
      >
        <Gift size={15} /> {t('giftPointsBtn')}
      </button>

      {/* Mutual friends count — plain text, no clickthrough in v1.
          Future polish: link to /friends/:userId/mutual list. */}
      {detail.mutualFriendsCount > 0 && (
        <p className={styles.mutual}>
          {t('mutual', { count: detail.mutualFriendsCount })}
        </p>
      )}

      {/* Top 5 badges — sorted by tier desc, then earned_at desc */}
      {detail.topBadges.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('topBadges')}</h2>
          <div className={styles.badgeRow}>
            {detail.topBadges.map((b) => (
              <div key={b.badgeId} className={styles.badgeCard} title={b.description}>
                <BadgeIcon
                  iconName={b.iconName ?? undefined}
                  colorTier={(b.colorTier as BadgeColorTier | null) ?? undefined}
                  size={48}
                />
                <span className={styles.badgeName}>{b.name}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Action row — Remove first (less severe), Block second
          (more severe). Both confirm before firing. */}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.removeBtn}
          onClick={handleRemove}
          disabled={pending}
        >
          <UserX size={14} /> {t('removeFriend')}
        </button>
        <button
          type="button"
          className={styles.blockBtn}
          onClick={handleBlock}
          disabled={pending}
        >
          <Ban size={14} /> {t('block')}
        </button>
      </div>

      {/* Portal-rendered (the .pageTransition transform wrapper would
          otherwise anchor its fixed backdrop off-position — the same
          trap RedemptionSheet documents). */}
      {giftOpen && (
        <GiftPointsSheet
          recipientUserId={detail.user.id}
          recipientName={displayName(detail, t)}
          onClose={() => setGiftOpen(false)}
        />
      )}
    </div>
  );
}

function displayName(d: FriendDetail, t: FriendDetailT): string {
  return d.user.username ?? d.user.name ?? t('nameFallback');
}

function StatTile({ icon, label, value }: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className={styles.statTile}>
      <span className={styles.statIcon}>{icon}</span>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}
